import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { PRM } from '@reflact/prmacweb';
import { KcBackendService } from '../konva-canvas/kc-backend.service';
import { PackService } from '../services/PackService';
import { PdfAnnotationService } from '../services/PdfAnnotationService';

export interface PageContext {
  page: number;
  docid: string;
  timeblockid: string;
  participantid?: string;
}

type pageAnnotation = {
  selectedDay: string,
  pack: PRM.IpadPackage,
  selectedeventobject: PRM.Event,
  dayEvents: PRM.Event[],
  allDocuments: PRM.IpadDocument[],
  selecteddoc: string,
  selectedDocObject: PRM.IpadDocument,
  page: string,
  dimensionsForTimeBlock: PRM.Dimension[],
  pageContexts: PageContext[],
  konvaJsons: any[],
  annotations: any[]
}

export const pageAnnotationResolver: ResolveFn<any> = async (route) => {
  const pageAnnotationService = inject(PdfAnnotationService);
  const kcService = inject(KcBackendService);
  const packService = inject(PackService);

  const data: pageAnnotation = {
    selectedDay: '',
    allDocuments: [],
    dayEvents: [],
    dimensionsForTimeBlock: [],
    pack: await packService.getCurrentPackage(),
    page: '',
    selecteddoc: '',
    selectedDocObject: undefined,
    selectedeventobject: undefined,
    annotations: [],
    konvaJsons: [],
    pageContexts: []
  }

  if (route.params.day) {
    data.selectedDay = route.params.day;
  }
  if (route.params.selectedevent) {
    data.selectedDay = route.parent.params.day;
  }
  if (data.selectedDay === '') {
    data.pack.eventdays.forEach((eventday) => {
      const result = mergeForDay(data, eventday.day, route);
      if (result) {
        if (result.allDocuments.length !== 0) data.allDocuments = data.allDocuments.concat(result.allDocuments);
        data.selectedDocObject = result.selectedDocObject;
        if (result.dimensionsForTimeBlock.length !== 0) data.dimensionsForTimeBlock = data.dimensionsForTimeBlock.concat(result.dimensionsForTimeBlock);
        data.selectedeventobject = result.selectedeventobject;
        if (result.dayEvents.length !== 0) data.dayEvents = data.dayEvents.concat(result.dayEvents);
      }
    });
  } else {
    const result = mergeForDay(data, data.selectedDay, route);
    if (result) {
      if (result.allDocuments.length !== 0) data.allDocuments = data.allDocuments.concat(result.allDocuments);
      data.selectedDocObject = result.selectedDocObject;
      if (result.dimensionsForTimeBlock.length !== 0) data.dimensionsForTimeBlock = data.dimensionsForTimeBlock.concat(result.dimensionsForTimeBlock);
      data.selectedeventobject = result.selectedeventobject;
      if (result.dayEvents.length !== 0) data.dayEvents = data.dayEvents.concat(result.dayEvents);
    }
  }

  let timeblockid = '';
  if (data.selectedeventobject) {
    timeblockid = data.selectedeventobject.timeblock._id;
  }
  let docid = '';
  if (data.selectedDocObject) {
    docid = data.selectedDocObject._id;
  }

  const pageContext: PageContext = {
    page: 0,
    docid,
    timeblockid,
    participantid: route.params.userid
  }

  if (!route.params.userid) {
    pageContext.participantid = '-1';
  }

  if (data.selectedDocObject) {
    if (!data.selectedDocObject.metadata.participantInstanceEnabled) {
      pageContext.participantid = null;
    }
  }

  if (!!pageContext.docid && !!pageContext.timeblockid) {
    const availableJsonPages: any = await kcService.getAvailableJsonOverlayPageNumbers(pageContext);
    const availableAnnotationPages: any = await pageAnnotationService.getAvailableAnnotationTextPageNumbers(pageContext.docid, pageContext.timeblockid, pageContext.participantid);
    for (let i = 0; i < data.selectedDocObject.metadata.pagescount; i++) {
      pageContext.page = i

      data.pageContexts.push({
        page: i,
        docid: pageContext.docid,
        timeblockid: pageContext.timeblockid,
        participantid: pageContext.participantid
      });

      if (availableJsonPages.indexOf(i.toString()) > -1) {
        const konvaJson: any = await kcService.loadJson(pageContext);
        data.konvaJsons.push(konvaJson);
      } else {
        data.konvaJsons.push({});
      }

      if (availableAnnotationPages.indexOf(i.toString()) > -1) {
        const annotationresult: any = await pageAnnotationService.getAnnotationText(pageContext.docid, i, pageContext.timeblockid, pageContext.participantid);
        data.annotations.push(annotationresult.value);
      } else {
        data.annotations.push('');
      }
    }
  }

  return data;
};

const mergeForDay = (data: pageAnnotation, day: string, route: ActivatedRouteSnapshot) => {
  const index = data.pack.eventdays.findIndex(ed => ed.day + '' === day + '');
  if (index > -1) {
    let eventsOnthisDay = data.pack.eventdays[index].events;
    let allparticipants: PRM.IpadParticipant[] = [];
    let currentEventIndex = 1;
    let selectedeventobject: PRM.Event;
    let allDocuments: PRM.IpadDocument[] = [];
    let selectedDocObject: PRM.IpadDocument;
    const dayEvents: PRM.Event[] = [];
    const dimensionsForTimeBlock: PRM.Dimension[] = [];

    eventsOnthisDay.forEach((ev: PRM.Event) => {
      if (!ev.timeblock) {
        return;
      }
      ev.participantId = [];
      ev.participantId.push(ev.resourceId);
      for (let i = currentEventIndex; i < eventsOnthisDay.length; i++) {
        const ev2: PRM.Event = eventsOnthisDay[i];
        if (ev.team === ev2.team && ev.start === ev2.start && ev.end === ev2.end) {
          if (ev.participantId.findIndex(id => id === ev2.resourceId) === -1) {
            ev.participantId.push(ev2.resourceId);
          }
          if (ev2.timeblock.documents) {
            if (!ev.timeblock.documents) {
              ev.timeblock.documents = [];
            }
            ev2.timeblock.documents.forEach((ev2doc) => {
              const docIndex = ev.timeblock.documents.findIndex(doc => doc._id === ev2doc._id);
              if (docIndex === -1) {
                ev.timeblock.documents.push(ev2doc);
              } else if (docIndex > -1 && ev2doc.metadata.participantInstanceEnabled === true) {
                if (ev.timeblock.documents.findIndex(doc => doc === ev2doc) === -1) {
                  const ev2PartiIndex = allparticipants.findIndex(parti => parti.id === ev2.resourceId);
                  if (ev2PartiIndex > -1) {
                    ev2doc.metadata.participantObject = allparticipants[ev2PartiIndex];
                  }
                  ev.timeblock.documents.push(ev2doc);
                }
              }
            });
          }
          ev2._id = 'null';
        }
      }
      currentEventIndex++;
      if (ev._id !== 'null') {
        dayEvents.push(ev);
      }
    });
    dayEvents.forEach((ev: PRM.Event) => {
      if (ev._id + '' === '' + route.params.selectedevent) {
        ev.documents.forEach((doc: PRM.IpadDocument) => {
          if (doc.metadata.participantInstanceEnabled && ev.resourceId != "-1") {
            const partiIndex = allparticipants.findIndex(parti => parti.id === ev.resourceId);
            if (partiIndex > -1) {
              doc.metadata.participantObject = allparticipants[partiIndex]
            }
          }
        });
        selectedeventobject = ev;
        if (ev.documents) {
          allDocuments = allDocuments.concat(ev.documents);
        }
        if (ev.timeblock.documents) {
          for (const id of ev.participantId) {
            const indexParticipant = allparticipants.findIndex(participant => participant.id === id);
            for (const document of ev.timeblock.documents) {
              if (document.metadata.participantInstanceEnabled === true) {
                if (!document.metadata.participantObject) {
                  document.metadata.participantObject = allparticipants[indexParticipant];
                }
              }
            }
          }
          allDocuments = allDocuments.concat(ev.timeblock.documents);
        }
        allDocuments = allDocuments.filter((value) => {
          return value != null;
        });
        selectedDocObject = allDocuments.find(doc => doc._id === route.params.selecteddoc + '');
        data.pack.dimensions.forEach((dimension: PRM.Dimension) => {
          const timeBlockDimension = { ...dimension };
          timeBlockDimension.anker = [];
          dimension.anker.forEach((anker) => {
            if (anker.timeblockids.indexOf(ev.timeblock._id) !== -1) {
              timeBlockDimension.anker.push(anker);
            }
          });
          if (timeBlockDimension.anker.length > 0) {
            dimensionsForTimeBlock.push(timeBlockDimension);
          }
        });
      }
    });
    return {
      dimensionsForTimeBlock,
      selectedDocObject,
      allDocuments,
      selectedeventobject,
      dayEvents
    }
  }
}